import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';
import type { SketchFace, SketchSolid } from './types';
import { solidToGeometry } from './primitives';
import { csgUnion, csgSubtract, csgIntersect } from './csgUtil';
import { meshToFaces } from './meshToSolid';

/**
 * Solid Tools engine (SketchUp's Solid Tools parity).
 *
 * Volume booleans over two or more solids: geometry goes through
 * three-bvh-csg, the triangle soup is repaired and rebuilt into analytic
 * faces by meshToFaces (T-junction splits + hole capping — a boolean seam
 * never kills the operation), and only an unrepairable shell rejects (the
 * caller keeps the pre-operation solids — "a broken solid is never
 * committed").
 *
 * Semantics per SketchUp:
 *   Union       — all selected solids merge into one cleaned volume.
 *   Subtract    — first solid minus the second (second is consumed).
 *   Intersect   — only the shared volume of both solids survives.
 *   Trim        — second solid is cut by the first; the cutter survives.
 *   Split       — both solids' pieces plus the intersection, all created.
 *   Outer Shell — union of everything with interior faces discarded (the
 *                 union already removes interior shells here).
 */

export interface SolidOpResult {
    ok: boolean;
    /** Rebuilt shells; each becomes one solid (Split returns several). */
    shells?: SketchFace[][];
    reason?: string;
}

const rebuild = (geometry: THREE.BufferGeometry): { ok: boolean; faces?: SketchFace[]; reason?: string } => {
    const result = meshToFaces(geometry);
    if (!result.ok) return { ok: false, reason: result.reason };
    return { ok: true, faces: result.faces.map((f) => ({ ...f, id: uuidv4() })) };
};

/** Merge every solid's geometry into one combined mesh (union operand). */
const combinedGeometry = (solids: SketchSolid[]): THREE.BufferGeometry => {
    const parts = solids.map((solid) => solidToGeometry(solid));
    if (parts.length === 1) return parts[0];
    // Accumulate through pairwise unions so the operand stays watertight
    // even when the individual solids already overlap each other.
    let acc = parts[0];
    for (let i = 1; i < parts.length; i++) acc = csgUnion(acc, parts[i]);
    return acc;
};

export function unionSolids(solids: SketchSolid[]): SolidOpResult {
    if (solids.length < 2) return { ok: false, reason: 'Select at least two solids.' };
    const result = rebuild(combinedGeometry(solids));
    if (!result.ok) return { ok: false, reason: result.reason };
    return { ok: true, shells: [result.faces!] };
}

/** Outer Shell: the union already drops interior partitions. */
export function outerShellSolids(solids: SketchSolid[]): SolidOpResult {
    return unionSolids(solids);
}

export function subtractSolids(minuend: SketchSolid, subtrahend: SketchSolid): SolidOpResult {
    const result = rebuild(csgSubtract(solidToGeometry(minuend), solidToGeometry(subtrahend)));
    if (!result.ok) return { ok: false, reason: result.reason };
    return { ok: true, shells: [result.faces!] };
}

export function intersectSolids(a: SketchSolid, b: SketchSolid): SolidOpResult {
    const result = rebuild(csgIntersect(solidToGeometry(a), solidToGeometry(b)));
    if (!result.ok) return { ok: false, reason: result.reason };
    return { ok: true, shells: [result.faces!] };
}

/** Trim: `target` loses the volume shared with `cutter`; cutter survives. */
export function trimSolids(cutter: SketchSolid, target: SketchSolid): SolidOpResult {
    return subtractSolids(target, cutter);
}

/**
 * Split: both inputs survive re-cut along the intersection boundary —
 * three shells: A minus B, the shared volume, B minus A. Empty pieces are
 * skipped (touching solids produce two pieces only).
 */
export function splitSolids(a: SketchSolid, b: SketchSolid): SolidOpResult {
    const geoA = solidToGeometry(a);
    const geoB = solidToGeometry(b);
    const pieces: SketchFace[][] = [];
    for (const geometry of [csgSubtract(geoA, geoB), csgIntersect(geoA, geoB), csgSubtract(geoB, geoA)]) {
        const result = rebuild(geometry);
        if (result.ok && result.faces!.length > 0) pieces.push(result.faces!);
    }
    if (pieces.length === 0) return { ok: false, reason: 'The solids do not overlap.' };
    return { ok: true, shells: pieces };
}
